import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { CELEBRATE_OPTIONS, calFetch, fmtGreg, fmtHebrew } from "@/lib/calApi";
import { useCalAuth } from "@/lib/calAuth";
import { CalLayout } from "@/components/calendar/CalLayout";
import { CalPushCard } from "@/components/calendar/CalPushCard";
import { HebrewDateFields, inputClass } from "@/components/calendar/HebrewDateFields";

function Card({ title, children, testId }) {
  return (
    <section data-testid={testId} className="rounded-3xl bg-cream border border-line p-5 sm:p-6 mb-5">
      <h3 className="text-xs uppercase tracking-[0.2em] text-amber-deep font-semibold mb-4">{title}</h3>
      {children}
    </section>
  );
}

function CelebratePicker({ value, onPick }) {
  return (
    <div className="space-y-2" role="radiogroup">
      {CELEBRATE_OPTIONS.map(([k, label, hint]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} data-testid={`cal-celebrate-${k}`} onClick={() => onPick(k)}
          className={`w-full text-left rounded-2xl border px-4 py-3 transition-colors ${value === k ? "bg-night text-white border-night" : "bg-paper border-line text-cocoa hover:border-amber-deep/50"}`}>
          <p className="font-semibold text-sm">{label}</p>
          <p className={`text-xs ${value === k ? "text-white/70" : "text-cocoa-soft"}`}>{hint}</p>
        </button>
      ))}
    </div>
  );
}

function ProposeForm({ person, pending, onSent }) {
  const [gdate, setGdate] = useState(person.gdate || "");
  const [hdate, setHdate] = useState(person.hdate || null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { note };
      if (gdate && gdate !== person.gdate) body.gdate = gdate;
      if (hdate?.day && JSON.stringify(hdate) !== JSON.stringify(person.hdate)) body.hdate = hdate;
      await calFetch("/cal/propose", { method: "POST", body });
      toast.success("¡Gracias! El Pollo lo revisa y lo aprueba");
      setNote("");
      onSent();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="space-y-3">
      {pending.length > 0 && <p data-testid="cal-pending-proposal" className="text-xs rounded-xl bg-candle/20 text-cocoa px-3 py-2">Tienes una corrección esperando aprobación del Pollo.</p>}
      <label className="block text-xs text-cocoa-soft">Fecha gregoriana
        <input type="date" data-testid="cal-propose-gdate" value={gdate} onChange={(e) => setGdate(e.target.value)} className={`${inputClass} mt-1`} />
      </label>
      <div className="text-xs text-cocoa-soft">Fecha hebrea <div className="mt-1"><HebrewDateFields value={hdate} onChange={setHdate} testId="cal-propose-hdate" /></div></div>
      <textarea data-testid="cal-propose-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={300} placeholder="Algún comentario (ej: nací después de la puesta de sol)" className={inputClass} />
      <button data-testid="cal-propose-submit" disabled={busy} className="rounded-full bg-amber-deep text-paper px-5 py-2.5 text-sm font-semibold cal-press disabled:opacity-50">Enviar corrección</button>
    </form>
  );
}

export default function CalPerfil() {
  const { logout } = useCalAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const load = () => calFetch("/cal/me").then(setMe).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const pick = async (celebrate) => {
    try {
      const r = await calFetch("/cal/me", { method: "POST", body: { celebrate } });
      setMe((m) => ({ ...m, person: r.person }));
      toast.success("Listo, guardamos tu preferencia");
    } catch (e) { toast.error(e.message); }
  };
  if (!me) return <CalLayout><div className="h-48 rounded-3xl bg-cream animate-pulse" /></CalLayout>;
  const p = me.person;

  return (
    <CalLayout>
      <div className="cal-rise mb-6">
        <p className="eyebrow text-[10px]">{p.branch}</p>
        <h2 data-testid="cal-profile-name" className="font-display italic text-cocoa text-4xl leading-tight">{p.name}</h2>
      </div>
      <Card title="Mis fechas" testId="cal-profile-dates">
        <dl className="grid grid-cols-2 gap-4">
          <div><dt className="text-xs text-cocoa-soft">Gregoriana</dt><dd data-testid="cal-profile-gdate" className="font-display text-xl text-cocoa">{fmtGreg(p.gdate)}</dd>{p.gdate_auto && <dd className="text-[11px] italic text-cocoa-soft">calculada desde la hebrea</dd>}</div>
          <div><dt className="text-xs text-cocoa-soft">Hebrea</dt><dd data-testid="cal-profile-hdate" className="font-display text-xl text-cocoa">{fmtHebrew(p.hdate)}</dd>{p.hdate_auto && <dd className="text-[11px] italic text-cocoa-soft">calculada desde la gregoriana (si naciste de noche, puede ser un día después)</dd>}</div>
        </dl>
        {p.status && <p className="text-xs text-cocoa-soft mt-3">Estado: {p.status}</p>}
      </Card>
      <Card title="¿Cómo quieres que te celebren?" testId="cal-profile-celebrate">
        <CelebratePicker value={p.celebrate || "both"} onPick={pick} />
      </Card>
      <Card title="Notificaciones" testId="cal-profile-push"><CalPushCard /></Card>
      <Card title="¿Tus fechas están mal?" testId="cal-profile-propose">
        <ProposeForm person={p} pending={me.pending_proposals || []} onSent={load} />
      </Card>
      <button data-testid="cal-logout-btn" onClick={() => { nav("/", { replace: true }); logout(); }}
        className="inline-flex items-center gap-2 text-sm text-cocoa-soft hover:text-cocoa mb-6"><LogOut className="w-4 h-4" /> Cerrar sesión del calendario</button>
    </CalLayout>
  );
}
